import types from '../mutation-types';
import KanbanColumnsAPI from '../../api/kanbanColumns';
import KanbanCardsAPI from '../../api/kanbanCards';
import ConversationApi from '../../api/inbox/conversation';
import { throwErrorMessage } from '../utils/api';
import { MESSAGE_TYPE } from 'shared/constants/messages';

export const DEFAULT_COLUMN_ID = 'default';

// O quadro é pessoal: cada um organiza as conversas pelas quais responde, e
// só o responsável pode mover um card. Enxergar a conversa na aba "Todas" —
// o que todo administrador faz — não a torna parte do quadro de quem olha.
const DEFAULT_COLUMN_ASSIGNEE_TYPE = 'me';

const sortCardsByPosition = cards =>
  [...cards].sort((a, b) => a.position - b.position);

// A conversation that just got a real card (through any path: the "add
// conversation" modal, a drag, or another tab via ActionCable) must not
// keep showing up in the virtual "all conversations" column too.
const removeFromDefaultColumn = ($state, conversationDisplayId) => {
  $state.defaultColumnConversations = $state.defaultColumnConversations.filter(
    card => card.conversation.display_id !== conversationDisplayId
  );
};

// A conversation can have at most one card, but it may show up more than
// once transiently (e.g. a stale entry still in the virtual column while a
// real one was just created), so this always returns every match rather
// than assuming there's exactly one.
const findCardsByConversationDisplayId = ($state, conversationDisplayId) => {
  const allCards = [
    ...$state.columns.flatMap(column => column.kanban_cards),
    ...$state.defaultColumnConversations,
  ];
  return allCards.filter(
    card => card.conversation.display_id === conversationDisplayId
  );
};

// The raw conversation list endpoint exposes only the display_id (the number
// shown to users) as `id` — there is no way to get the real DB id from it.
// Real KanbanCard payloads have both (see _kanban_card.json.jbuilder); here
// `conversation.id` and `conversation.display_id` end up holding the same
// value, which is fine since nothing in the UI reads `conversation.id` for
// anything other than display.
const mapConversationToDefaultCard = (conversation, inboxesById = {}) => {
  const inbox = inboxesById[conversation.inbox_id] || {};
  const sender = conversation.meta?.sender || {};
  return {
    id: `${DEFAULT_COLUMN_ID}-${conversation.id}`,
    kanban_column_id: DEFAULT_COLUMN_ID,
    position: 0,
    added_at: conversation.timestamp,
    conversation: {
      id: conversation.id,
      display_id: conversation.id,
      contact: {
        id: sender.id,
        name: sender.name,
        phone_number: sender.phone_number,
        thumbnail: sender.thumbnail,
      },
      inbox: {
        id: conversation.inbox_id,
        name: inbox.name || '',
        // list endpoint nests it under meta.channel; the ActionCable
        // conversation.created payload has it top-level as `channel`.
        channel_type:
          conversation.meta?.channel ||
          conversation.channel ||
          inbox.channel_type ||
          '',
      },
      last_activity_at: conversation.timestamp,
      labels: conversation.labels || [],
      unread_count: conversation.unread_count || 0,
      messages: conversation.messages || [],
      last_non_activity_message: conversation.last_non_activity_message,
    },
  };
};

// A API devolve 25 conversas por página (CONVERSATION_RESULTS_PER_PAGE), então
// sem paginar a coluna padrão mostrava só as 25 mais recentes, por mais que o
// agente rolasse a lista.
const loadDefaultColumnPage = async ({ rootGetters, localGetters, page }) => {
  const {
    data: { data },
  } = await ConversationApi.get({
    status: 'open',
    assigneeType: DEFAULT_COLUMN_ASSIGNEE_TYPE,
    sortBy: 'last_activity_at_desc',
    page,
  });

  const inboxesById = Object.fromEntries(
    (rootGetters['inboxes/getInboxes'] || []).map(inbox => [inbox.id, inbox])
  );
  const cardedDisplayIds = new Set(
    localGetters.getAllCardConversationDisplayIds
  );
  const payload = data.payload || [];

  return {
    cards: payload
      .filter(conversation => !cardedDisplayIds.has(conversation.id))
      .map(conversation =>
        mapConversationToDefaultCard(conversation, inboxesById)
      ),
    // Uma página inteira pode sumir no filtro acima, então a condição de
    // parada é a resposta da API vir vazia, não a lista de cards.
    hasMore: payload.length > 0,
    // Mesmo contador que a aba "Atribuídas a mim" mostra na tela de conversas.
    total: data.meta?.mine_count || 0,
  };
};

export const state = {
  columns: [],
  defaultColumnConversations: [],
  defaultColumnPage: 1,
  defaultColumnHasMore: true,
  // Quantas conversas abertas estão atribuídas a mim, direto da API. O que a
  // coluna virtual exibe é este número menos as que já viraram card — ver
  // `getDefaultColumnTotal`.
  assignedOpenTotal: 0,
  uiFlags: {
    isFetching: false,
    isCreatingColumn: false,
    isFetchingDefaultColumn: false,
    isFetchingMoreDefaultColumn: false,
  },
};

export const getters = {
  getColumns($state) {
    return $state.columns;
  },
  getDefaultColumnCards($state) {
    return $state.defaultColumnConversations;
  },
  getDefaultColumnHasMore($state) {
    return $state.defaultColumnHasMore;
  },
  // A coluna virtual lista só o que ainda não foi fichado, então o cabeçalho
  // dela não pode mostrar o total de conversas atribuídas a mim: ao arrastar um
  // card para outra coluna o número tem que cair junto.
  getDefaultColumnTotal($state) {
    const cardedCount = $state.columns.reduce(
      (count, column) => count + column.kanban_cards.length,
      0
    );
    return Math.max(0, $state.assignedOpenTotal - cardedCount);
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getAllCardConversationDisplayIds($state) {
    return $state.columns.flatMap(column =>
      column.kanban_cards.map(card => card.conversation.display_id)
    );
  },
};

export const actions = {
  fetchColumns: async ({ commit }) => {
    commit(types.SET_KANBAN_UI_FLAG, { isFetching: true });
    try {
      const response = await KanbanColumnsAPI.get();
      commit(types.SET_KANBAN_COLUMNS, response.data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetching: false });
    }
  },
  createColumn: async ({ commit }, { name, color }) => {
    commit(types.SET_KANBAN_UI_FLAG, { isCreatingColumn: true });
    try {
      const response = await KanbanColumnsAPI.create({
        kanban_column: { name, color },
      });
      commit(types.ADD_KANBAN_COLUMN, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isCreatingColumn: false });
    }
  },
  renameColumn: async ({ commit }, { id, name }) => {
    try {
      const response = await KanbanColumnsAPI.update(id, {
        kanban_column: { name },
      });
      commit(types.UPDATE_KANBAN_COLUMN, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    }
  },
  updateColumnColor: async ({ commit }, { id, color }) => {
    try {
      const response = await KanbanColumnsAPI.update(id, {
        kanban_column: { color },
      });
      commit(types.UPDATE_KANBAN_COLUMN, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    }
  },
  deleteColumn: async ({ commit }, id) => {
    try {
      await KanbanColumnsAPI.delete(id);
      commit(types.REMOVE_KANBAN_COLUMN, id);
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    }
  },
  addCard: async ({ commit }, { kanbanColumnId, conversationDisplayId }) => {
    try {
      const response = await KanbanCardsAPI.create({
        kanban_card: {
          kanban_column_id: kanbanColumnId,
          conversation_display_id: conversationDisplayId,
        },
      });
      commit(types.ADD_KANBAN_CARD, response.data);
      // O servidor é a autoridade sobre onde o card ficou. Se ele respondeu
      // outra coluna, a mutation pode nem ter conseguido posicionar o card
      // (coluna fora do quadro deste usuário) e o card sumiria sem aviso.
      if (response.data.kanban_column_id !== kanbanColumnId) {
        // Sem mensagem de propósito: o texto vem do i18n na camada de UI.
        throw new Error();
      }
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    }
  },
  moveCard: async (
    { commit, state: kanbanState },
    { cardId, kanbanColumnId, position }
  ) => {
    const current = kanbanState.columns
      .flatMap(column => column.kanban_cards)
      .find(card => card.id === cardId);
    // Cópia rasa: a mutation faz `Object.assign` no card do state, então
    // guardar a referência não serviria para desfazer o movimento.
    const previousCard = current ? { ...current } : null;

    // O patch otimista carrega o card inteiro, não só os campos que mudaram:
    // mover para outra coluna é uma remoção seguida de inserção, e um payload
    // parcial entraria na coluna de destino sem `conversation`, quebrando o
    // render do quadro até a resposta da API chegar.
    if (previousCard) {
      commit(types.UPDATE_KANBAN_CARD, {
        ...previousCard,
        kanban_column_id: kanbanColumnId,
        position,
      });
    }

    try {
      const response = await KanbanCardsAPI.update(cardId, {
        kanban_card: { kanban_column_id: kanbanColumnId, position },
      });
      commit(types.UPDATE_KANBAN_CARD, response.data);
    } catch (error) {
      if (previousCard) {
        commit(types.UPDATE_KANBAN_CARD, previousCard);
      }
      throwErrorMessage(error);
      throw error;
    }
  },
  removeCard: async ({ commit, dispatch }, { id, kanbanColumnId }) => {
    try {
      await KanbanCardsAPI.delete(id);
      commit(types.REMOVE_KANBAN_CARD, { id, kanbanColumnId });
      // The conversation is open and uncarded again — it belongs back in the
      // virtual "all conversations" column.
      dispatch('fetchDefaultColumnConversations');
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    }
  },
  fetchDefaultColumnConversations: async ({
    commit,
    rootGetters,
    getters: localGetters,
  }) => {
    commit(types.SET_KANBAN_UI_FLAG, { isFetchingDefaultColumn: true });
    try {
      const { cards, hasMore, total } = await loadDefaultColumnPage({
        rootGetters,
        localGetters,
        page: 1,
      });
      commit(types.SET_DEFAULT_COLUMN_CONVERSATIONS, {
        cards,
        page: 1,
        hasMore,
        total,
      });
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetchingDefaultColumn: false });
    }
  },
  fetchMoreDefaultColumnConversations: async ({
    commit,
    state: $state,
    rootGetters,
    getters: localGetters,
  }) => {
    const { isFetchingDefaultColumn, isFetchingMoreDefaultColumn } =
      $state.uiFlags;
    if (
      !$state.defaultColumnHasMore ||
      isFetchingDefaultColumn ||
      isFetchingMoreDefaultColumn
    ) {
      return;
    }

    const page = $state.defaultColumnPage + 1;
    commit(types.SET_KANBAN_UI_FLAG, { isFetchingMoreDefaultColumn: true });
    try {
      const { cards, hasMore, total } = await loadDefaultColumnPage({
        rootGetters,
        localGetters,
        page,
      });
      commit(types.APPEND_DEFAULT_COLUMN_CONVERSATIONS, {
        cards,
        page,
        hasMore,
        total,
      });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetchingMoreDefaultColumn: false });
    }
  },
  prependDefaultColumnConversation: ({ commit, rootGetters }, conversation) => {
    if (!conversation || conversation.status !== 'open') return;
    // Mesmo critério de `loadDefaultColumnPage`: só entra no quadro o que é
    // meu. Uma conversa recém-criada normalmente ainda não tem responsável.
    if (conversation.meta?.assignee?.id !== rootGetters.getCurrentUserID) {
      return;
    }
    const inboxesById = Object.fromEntries(
      (rootGetters['inboxes/getInboxes'] || []).map(inbox => [inbox.id, inbox])
    );
    commit(
      types.PREPEND_DEFAULT_COLUMN_CONVERSATION,
      mapConversationToDefaultCard(conversation, inboxesById)
    );
  },
  updateLastMessageFromCable: (
    { commit },
    { conversationDisplayId, message }
  ) => {
    if (!conversationDisplayId || !message) return;
    commit(types.UPDATE_CARD_LAST_MESSAGE, { conversationDisplayId, message });
  },
  updateCardLabelsFromCable: (
    { commit },
    { conversationDisplayId, labels }
  ) => {
    if (!conversationDisplayId || !labels) return;
    commit(types.UPDATE_CARD_LABELS, { conversationDisplayId, labels });
  },
  // O contador de não lidas é sempre o que o servidor mandou — tanto ao ler a
  // conversa (zera) quanto ao receber mensagem (sobe). Somar no cliente
  // dessincronizaria do badge da tela de conversas.
  updateCardUnreadCountFromCable: (
    { commit },
    { conversationDisplayId, unreadCount }
  ) => {
    if (!conversationDisplayId || unreadCount === undefined) return;
    commit(types.UPDATE_CARD_UNREAD_COUNT, {
      conversationDisplayId,
      unreadCount,
    });
  },
  upsertColumnFromCable: ({ commit }, data) => {
    commit(types.UPDATE_KANBAN_COLUMN, { kanban_cards: [], ...data });
  },
  removeColumnFromCable: ({ commit }, data) => {
    commit(types.REMOVE_KANBAN_COLUMN, data.id);
  },
  upsertCardFromCable: ({ commit }, data) => {
    commit(types.UPDATE_KANBAN_CARD, data);
  },
  removeCardFromCable: ({ commit, dispatch }, data) => {
    commit(types.REMOVE_KANBAN_CARD, {
      id: data.id,
      kanbanColumnId: data.kanban_column_id,
    });
    dispatch('fetchDefaultColumnConversations');
  },
};

export const mutations = {
  [types.SET_KANBAN_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.SET_KANBAN_COLUMNS]($state, columns) {
    $state.columns = columns.map(column => ({
      ...column,
      kanban_cards: sortCardsByPosition(column.kanban_cards || []),
    }));
  },
  [types.ADD_KANBAN_COLUMN]($state, column) {
    if ($state.columns.some(existing => existing.id === column.id)) return;
    $state.columns.push({ ...column, kanban_cards: column.kanban_cards || [] });
  },
  [types.UPDATE_KANBAN_COLUMN]($state, column) {
    const existing = $state.columns.find(c => c.id === column.id);
    if (!existing) {
      $state.columns.push({
        ...column,
        kanban_cards: column.kanban_cards || [],
      });
      return;
    }
    Object.assign(existing, column, {
      kanban_cards:
        column.kanban_cards !== undefined
          ? sortCardsByPosition(column.kanban_cards)
          : existing.kanban_cards,
    });
  },
  [types.REMOVE_KANBAN_COLUMN]($state, id) {
    $state.columns = $state.columns.filter(column => column.id !== id);
  },
  [types.ADD_KANBAN_CARD]($state, card) {
    const column = $state.columns.find(c => c.id === card.kanban_column_id);
    if (!column) return;
    // Also strips the synthetic default-column placeholder (if the card was
    // just dragged in from there) so it isn't left duplicated alongside the
    // real card the server just returned.
    const withoutDuplicates = column.kanban_cards.filter(
      existing =>
        existing.id !== card.id &&
        existing.conversation.display_id !== card.conversation.display_id
    );
    column.kanban_cards = sortCardsByPosition([...withoutDuplicates, card]);
    removeFromDefaultColumn($state, card.conversation.display_id);
  },
  [types.UPDATE_KANBAN_CARD]($state, card) {
    // Guarda o card como ele estava antes, em qualquer coluna, para que um
    // payload parcial (patch otimista, broadcast enxuto) atualize os campos
    // que vieram sem descartar o resto — em especial `conversation`, de que
    // o render das colunas depende.
    let previous = null;
    $state.columns.forEach(column => {
      const index = column.kanban_cards.findIndex(c => c.id === card.id);
      if (index === -1) return;
      previous = column.kanban_cards[index];
      if (column.id !== card.kanban_column_id) {
        column.kanban_cards = column.kanban_cards.filter(c => c.id !== card.id);
      }
    });

    const targetColumn = $state.columns.find(
      c => c.id === card.kanban_column_id
    );
    if (!targetColumn) return;

    const existing = targetColumn.kanban_cards.find(c => c.id === card.id);
    if (existing) {
      Object.assign(existing, card);
    } else {
      const merged = { ...(previous || {}), ...card };
      // Um card sem `conversation` não tem como ser renderizado e derrubaria
      // o quadro inteiro; melhor ignorá-lo do que envenenar o state.
      if (!merged.conversation) return;
      targetColumn.kanban_cards.push(merged);
    }
    targetColumn.kanban_cards = sortCardsByPosition(targetColumn.kanban_cards);
    // O payload pode ser parcial; nesse caso a conversa vem do card que já
    // estava no state.
    const displayId =
      card.conversation?.display_id || previous?.conversation?.display_id;
    if (displayId) {
      removeFromDefaultColumn($state, displayId);
    }
  },
  // Remove por id, sem confiar na coluna informada: quando um card é
  // reivindicado por outro agente, o broadcast que chega ao dono anterior já
  // aponta para a coluna nova, que não existe no quadro dele.
  [types.REMOVE_KANBAN_CARD]($state, { id }) {
    $state.columns.forEach(column => {
      if (!column.kanban_cards.some(card => card.id === id)) return;
      column.kanban_cards = column.kanban_cards.filter(card => card.id !== id);
    });
  },
  [types.SET_DEFAULT_COLUMN_CONVERSATIONS](
    $state,
    { cards, page, hasMore, total }
  ) {
    $state.defaultColumnConversations = cards;
    $state.defaultColumnPage = page;
    $state.defaultColumnHasMore = hasMore;
    $state.assignedOpenTotal = total;
  },
  [types.APPEND_DEFAULT_COLUMN_CONVERSATIONS](
    $state,
    { cards, page, hasMore, total }
  ) {
    const alreadyPresent = new Set(
      $state.defaultColumnConversations.map(
        card => card.conversation.display_id
      )
    );
    $state.defaultColumnConversations = [
      ...$state.defaultColumnConversations,
      ...cards.filter(
        card => !alreadyPresent.has(card.conversation.display_id)
      ),
    ];
    $state.defaultColumnPage = page;
    $state.defaultColumnHasMore = hasMore;
    $state.assignedOpenTotal = total;
  },
  [types.PREPEND_DEFAULT_COLUMN_CONVERSATION]($state, card) {
    const alreadyPresent = $state.defaultColumnConversations.some(
      existing =>
        existing.conversation.display_id === card.conversation.display_id
    );
    if (alreadyPresent) return;
    $state.defaultColumnConversations = [
      card,
      ...$state.defaultColumnConversations,
    ];
    // O total veio do contador da API na carga inicial e não é recarregado a
    // cada conversa nova que chega pelo cable. Sem incrementar aqui, o badge
    // congela no valor do momento em que o quadro abriu e vai ficando cada
    // vez mais atrás do número da tela de conversas.
    $state.assignedOpenTotal += 1;
  },
  [types.UPDATE_CARD_LAST_MESSAGE]($state, { conversationDisplayId, message }) {
    findCardsByConversationDisplayId($state, conversationDisplayId).forEach(
      card => {
        card.conversation.messages = [message];
        card.conversation.last_activity_at = message.created_at;
        // Activity messages (status changes, assignment, etc.) never replace
        // the "real" last message shown in the preview — only genuine
        // incoming/outgoing/template messages do.
        if (message.message_type !== MESSAGE_TYPE.ACTIVITY) {
          card.conversation.last_non_activity_message = message;
        }
        // A própria mensagem carrega o contador recalculado pelo servidor
        // (Message#push_event_data), então o badge sobe junto com a prévia.
        if (message.conversation?.unread_count !== undefined) {
          card.conversation.unread_count = message.conversation.unread_count;
        }
      }
    );
  },
  [types.UPDATE_CARD_LABELS]($state, { conversationDisplayId, labels }) {
    findCardsByConversationDisplayId($state, conversationDisplayId).forEach(
      card => {
        card.conversation.labels = labels;
      }
    );
  },
  [types.UPDATE_CARD_UNREAD_COUNT](
    $state,
    { conversationDisplayId, unreadCount }
  ) {
    findCardsByConversationDisplayId($state, conversationDisplayId).forEach(
      card => {
        card.conversation.unread_count = unreadCount;
      }
    );
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
